import * as THREE from "three";

import type { ProjectileStyle } from "../content/schemas";
import { elementTint } from "../content/weapon-mods";
import { WEAPONS } from "../content/weapons";
import type { ProjectileState } from "../simulation/snapshot";

export const PROJECTILE_COLORS: Record<ProjectileStyle, number> = {
  pellet: 0xe8c878,
  slug: 0xf0e0b0,
  bolt: 0x6ec6ff,
  fork: 0x8ad4ff,
  pulse: 0xb48cff,
  rail: 0xf4d88a,
  disc: 0x72f0d0,
  torpedo: 0xff8a4a,
  swarm: 0xffb060,
  charge: 0xd45cff,
  singularity: 0xc777ff,
};

const HOSTILE_COLOR = 0xff6b4a;

/** Visual family for mesh selection (render-only). */
export type ProjectileFamily = "cone" | "bolt" | "warhead";

export function projectileFamily(style: ProjectileStyle): ProjectileFamily {
  switch (style) {
    case "pellet":
    case "slug":
      return "cone";
    case "bolt":
    case "fork":
    case "pulse":
    case "rail":
    case "disc":
      return "bolt";
    case "torpedo":
    case "swarm":
    case "charge":
    case "singularity":
      return "warhead";
  }
}

type StyleDims = {
  /** Body length along local +X (nose) — anchors trail placement. */
  length: number;
  /** Optional trail length behind warheads; 0 = none. */
  trail: number;
};

const STYLE_DIMS: Record<ProjectileStyle, StyleDims> = {
  pellet: { length: 0.72, trail: 0 },
  slug: { length: 1.05, trail: 0 },
  bolt: { length: 1.35, trail: 0 },
  fork: { length: 1.2, trail: 0 },
  pulse: { length: 1.1, trail: 0 },
  rail: { length: 2.15, trail: 0 },
  disc: { length: 0.95, trail: 0 },
  torpedo: { length: 1.85, trail: 2.4 },
  swarm: { length: 1.15, trail: 1.5 },
  charge: { length: 1.55, trail: 2.8 },
  singularity: { length: 1.4, trail: 2.2 },
};

type PooledShot = {
  root: THREE.Group;
  body: THREE.Mesh;
  trail: THREE.Mesh;
  bodyMat: THREE.MeshStandardMaterial;
  trailMat: THREE.MeshBasicMaterial;
  style: ProjectileStyle | null;
  lastColor: number;
  lastScale: number;
};

/**
 * Pooled, style-aware projectiles for top-down combat readability.
 * Kinetic = short cones, arc = stretched bolts, ordnance = fat capsules + trails.
 */
export class ProjectileField {
  private readonly group = new THREE.Group();
  private readonly pool: PooledShot[] = [];
  private readonly shared = createSharedGeometries();

  public constructor(scene: THREE.Scene) {
    this.group.name = "projectiles";
    scene.add(this.group);
  }

  public sync(shots: readonly ProjectileState[], affixTint: number | null): void {
    while (this.pool.length < shots.length) {
      this.pool.push(this.createVisual());
    }

    for (let i = 0; i < this.pool.length; i += 1) {
      const visual = this.pool[i]!;
      const shot = shots[i];
      if (!shot) {
        visual.root.visible = false;
        continue;
      }
      applyShot(visual, shot, affixTint, this.shared);
    }
  }

  public dispose(): void {
    for (const visual of this.pool) {
      this.group.remove(visual.root);
      visual.bodyMat.dispose();
      visual.trailMat.dispose();
    }
    this.pool.length = 0;
    disposeSharedGeometries(this.shared);
    this.group.removeFromParent();
  }

  private createVisual(): PooledShot {
    // Standard + emissive so bloom / env see weapon tints; toneMapped off keeps pops crisp.
    const bodyMat = new THREE.MeshStandardMaterial({
      color: 0xffe08a,
      emissive: 0xffe08a,
      emissiveIntensity: 1.35,
      metalness: 0.15,
      roughness: 0.35,
      toneMapped: false,
    });
    const trailMat = new THREE.MeshBasicMaterial({
      color: 0xff8a4a,
      transparent: true,
      opacity: 0.42,
      depthWrite: false,
      toneMapped: false,
    });

    const body = new THREE.Mesh(this.shared.conePellet, bodyMat);
    body.rotation.z = -Math.PI / 2; // tip / capsule long axis → local +X

    const trail = new THREE.Mesh(this.shared.trailBox, trailMat);
    trail.visible = false;
    trail.position.x = -0.8;

    const root = new THREE.Group();
    root.add(body, trail);
    root.visible = false;
    this.group.add(root);

    return { root, body, trail, bodyMat, trailMat, style: null, lastColor: -1, lastScale: -1 };
  }
}

type SharedGeos = {
  conePellet: THREE.BufferGeometry;
  coneSlug: THREE.BufferGeometry;
  boltThin: THREE.BufferGeometry;
  boltFat: THREE.BufferGeometry;
  boltRail: THREE.BufferGeometry;
  discRing: THREE.BufferGeometry;
  warheadMid: THREE.BufferGeometry;
  warheadFat: THREE.BufferGeometry;
  warheadHuge: THREE.BufferGeometry;
  trailBox: THREE.BufferGeometry;
};

function createSharedGeometries(): SharedGeos {
  // Cones: tip +Y before body.rotation.z flip → local +X nose.
  const conePellet = new THREE.ConeGeometry(0.22, 0.72, 5);
  const coneSlug = new THREE.ConeGeometry(0.3, 1.05, 5);
  // Capsules along Y; same z-rot points them along travel.
  const boltThin = new THREE.CapsuleGeometry(0.11, 1.05, 2, 6);
  const boltFat = new THREE.CapsuleGeometry(0.3, 0.55, 2, 6);
  const boltRail = new THREE.CapsuleGeometry(0.08, 1.9, 2, 6);
  const discRing = new THREE.TorusGeometry(0.42, 0.13, 6, 16).rotateX(Math.PI / 2);
  const warheadMid = new THREE.CapsuleGeometry(0.26, 0.65, 2, 6);
  const warheadFat = new THREE.CapsuleGeometry(0.42, 1.05, 3, 7);
  const warheadHuge = new THREE.CapsuleGeometry(0.55, 0.7, 3, 7);
  // Unit box scaled per-shot for smoke streak.
  const trailBox = new THREE.BoxGeometry(1, 0.14, 0.14);
  return {
    conePellet,
    coneSlug,
    boltThin,
    boltFat,
    boltRail,
    discRing,
    warheadMid,
    warheadFat,
    warheadHuge,
    trailBox,
  };
}

function disposeSharedGeometries(shared: SharedGeos): void {
  for (const geo of Object.values(shared)) geo.dispose();
}

function geometryForStyle(style: ProjectileStyle, shared: SharedGeos): THREE.BufferGeometry {
  switch (style) {
    case "pellet":
      return shared.conePellet;
    case "slug":
      return shared.coneSlug;
    case "bolt":
    case "fork":
      return shared.boltThin;
    case "rail":
      return shared.boltRail;
    case "disc":
      return shared.discRing;
    case "pulse":
      return shared.boltFat;
    case "swarm":
      return shared.warheadMid;
    case "torpedo":
      return shared.warheadFat;
    case "charge":
    case "singularity":
      return shared.warheadHuge;
  }
}

function resolveStyle(shot: ProjectileState): ProjectileStyle {
  if (!shot.friendly) {
    // Hostile shots: fat blob for bombers, bolt otherwise.
    return shot.radius > 1.2 ? "torpedo" : "bolt";
  }
  return WEAPONS[shot.weaponId]?.projectileStyle ?? "pellet";
}

function applyShot(
  visual: PooledShot,
  shot: ProjectileState,
  affixTint: number | null,
  shared: SharedGeos,
): void {
  const style = resolveStyle(shot);
  const dims = STYLE_DIMS[style];
  const family = projectileFamily(style);

  if (visual.style !== style) {
    visual.body.geometry = geometryForStyle(style, shared);
    visual.body.rotation.z = style === "disc" ? 0 : -Math.PI / 2;
    visual.style = style;
  }

  const payloadTint = shot.friendly ? elementTint(shot.element) : null;
  const color = shot.friendly
    ? (payloadTint ?? affixTint ?? PROJECTILE_COLORS[style])
    : HOSTILE_COLOR;
  // Color + emissive stay in sync so weapon-mod / affix tints bloom correctly.
  if (color !== visual.lastColor) {
    visual.bodyMat.color.setHex(color);
    visual.bodyMat.emissive.setHex(color);
    visual.trailMat.color.setHex(color);
    visual.lastColor = color;
  }
  visual.bodyMat.emissiveIntensity = shot.friendly ? 1.35 : 1.15;

  // Radius >1 (ordnance / bomber) reads larger without drowning the silhouette.
  const radiusBoost = shot.radius > 1 ? Math.min(2.4, 0.85 + shot.radius * 0.35) : 1;
  const scale =
    family === "warhead"
      ? radiusBoost
      : family === "bolt" && style === "pulse"
        ? Math.max(1.15, radiusBoost)
        : radiusBoost > 1
          ? radiusBoost
          : 1;
  if (scale !== visual.lastScale) {
    visual.body.scale.setScalar(scale);
    visual.lastScale = scale;
  }

  const speed = Math.hypot(shot.vx, shot.vy);
  const yaw = speed > 0.01 ? Math.atan2(shot.vy, shot.vx) : 0;
  visual.root.position.set(shot.x, shot.altitude, shot.y);
  visual.root.rotation.y = -yaw;
  visual.root.visible = true;

  if (dims.trail > 0) {
    const trailLen = dims.trail * (0.85 + Math.min(1, speed / 80) * 0.35) * scale;
    visual.trail.visible = true;
    visual.trail.scale.set(trailLen, scale, scale);
    visual.trail.position.x = -dims.length * 0.35 * scale - trailLen * 0.5;
    visual.trailMat.opacity = shot.friendly ? 0.4 : 0.32;
  } else {
    visual.trail.visible = false;
  }
}
